import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { doc, getDoc, collection, getDocs, addDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { showError, showSuccess } from '../utils/notifications';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';

function TicketsConsorcio() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [ticketsData, setTicketsData] = useState(null);
  const [recargasData, setRecargasData] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [salesReport, setSalesReport] = useState(null);
  const [activeSection, setActiveSection] = useState('tickets-consorcio');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState({
    id: true,
    fechaOperacion: true,
    amount: true,
    description: true,
    executedBy: true,
    newTicketsAuto: true,
    numberCa: true,
    paymentMethod: true,
    price: true,
    status: true,
    telefono: true,
    ticketsAdded: true,
    userName: true,
    vehicleType: true,
    token: true
  });
  
  // Modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [porcentajeMotos, setPorcentajeMotos] = useState('45');
  const [porcentajeVehiculos, setPorcentajeVehiculos] = useState('55');
  const [requestPassword, setRequestPassword] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  
  // Historial states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyAmount, setHistoryAmount] = useState('');
  const [historyPorcentajeMotos, setHistoryPorcentajeMotos] = useState('45');
  const [historyPorcentajeVehiculos, setHistoryPorcentajeVehiculos] = useState('55');
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Purchase history states
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);

  // Purchase report date filter states
  const [purchaseDateFilter, setPurchaseDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [filteredPurchaseHistory, setFilteredPurchaseHistory] = useState([]);

  // Format timestamp helper function - convierte cualquier formato a fecha legible
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
        if (isNaN(date.getTime())) return String(timestamp);
      } else {
        return String(timestamp);
      }
      
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) + ' ' + date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Function to calculate available tickets
  const calculateAvailableTickets = () => {
    try {
      const totalMotosCompradas = purchaseHistory.reduce((total, request) => {
        return total + (parseInt(request.ticketsMotos) || 0);
      }, 0);

      const totalVehiculosComprados = purchaseHistory.reduce((total, request) => {
        return total + (parseInt(request.ticketsVehiculos) || 0);
      }, 0);

      const totalMotosVendidas = salesReport ? salesReport.ticketsMotoVendidos : 0;
      const totalVehiculosVendidos = salesReport ? salesReport.ticketsAutoVendidos : 0;

      const motosDisponibles = totalMotosCompradas - totalMotosVendidas;
      const vehiculosDisponibles = totalVehiculosComprados - totalVehiculosVendidos;

      return {
        cantidadMoto: motosDisponibles,
        cantidadAuto: vehiculosDisponibles,
        totalMotosCompradas,
        totalVehiculosComprados,
        totalMotosVendidas,
        totalVehiculosVendidos,
        updatedAt: new Date(),
        ultimaActualizacion: new Date().toLocaleString('es-ES')
      };
    } catch (error) {
      console.error('❌ Error calculando tickets disponibles:', error);
      return {
        cantidadMoto: 0, cantidadAuto: 0,
        totalMotosCompradas: 0, totalVehiculosComprados: 0,
        totalMotosVendidas: 0, totalVehiculosVendidos: 0,
        updatedAt: new Date(), ultimaActualizacion: new Date().toLocaleString('es-ES')
      };
    }
  };

  // Function to get recargas data
  const fetchRecargasData = async () => {
    try {
      console.log('📊 Obteniendo datos de recargas...');

      // Cargar IDs de Recharge para detectar duplicados por document ID
      const rechargeSnap = await getDocs(collection(db, 'Recharge'));
      const idsEnRecharge = new Set();
      rechargeSnap.forEach((d) => idsEnRecharge.add(d.id));
      console.log(`🔍 IDs en Recharge: ${idsEnRecharge.size}`);

      const querySnapshot = await getDocs(collection(db, 'RechargeInternas'));
      const recargas = [];
      querySnapshot.forEach((doc) => {
        recargas.push({ id: doc.id, ...doc.data() });
      });
      console.log(`📊 Recargas obtenidas: ${recargas.length}`);

      // Para el resumen: excluir los que tienen el mismo ID en Recharge (duplicados)
      const recargasParaResumen = recargas.filter(r => !idsEnRecharge.has(r.id));
      console.log(`📊 Para resumen (sin duplicados en Recharge): ${recargasParaResumen.length}`);

      // Apply date filter if set
      let filteredRecargas = recargas;
      let filteredParaResumen = recargasParaResumen;
      if (dateFilter.startDate || dateFilter.endDate) {
        const applyDateFilter = (lista) => lista.filter(recarga => {
          let recargaDate = null;
          if (recarga.date) recargaDate = new Date(recarga.date.seconds * 1000);
          else if (recarga.createdAt) recargaDate = new Date(recarga.createdAt);
          else if (recarga.fecha) recargaDate = new Date(recarga.fecha);
          if (!recargaDate) return false;
          const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
          const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;
          if (startDate && recargaDate < startDate) return false;
          if (endDate && recargaDate > endDate) return false;
          return true;
        });
        filteredRecargas = applyDateFilter(recargas);
        filteredParaResumen = applyDateFilter(recargasParaResumen);
        console.log(`📊 Con filtro fecha — tabla: ${filteredRecargas.length}, resumen: ${filteredParaResumen.length}`);
      }

      // Ordenar por fecha descendente (más reciente primero)
      filteredRecargas.sort((a, b) => {
        const getTs = (r) => r.date?.seconds ?? r.createdAt?.seconds ?? r.timestamp?.seconds ?? 0;
        return getTs(b) - getTs(a);
      });

      // Tabla: todos los registros de RechargeInternas
      setRecargasData(filteredRecargas);
      // Resumen: solo los no duplicados en Recharge
      generateSalesReport(filteredParaResumen);
    } catch (error) {
      console.error('❌ Error obteniendo recargas:', error);
      showError('Error', 'No se pudieron obtener los datos de recargas');
    }
  };

  // Function to generate comprehensive sales report
  const generateSalesReport = (recargas) => {
    try {
      const report = {
        totalRecargas: recargas.length,
        totalIngresos: 0,
        totalTicketsVendidos: 0,
        ticketsAutoVendidos: 0,
        ticketsMotoVendidos: 0,
        recargasAuto: 0,
        recargasMoto: 0,
        promedioPrecio: 0,
        recargasPorDia: {},
        recargasPorCA: {},
        recargasPorVendedor: {},
        topVendedores: [],
        topCAs: [],
        recargasRecientes: []
      };

      // Process each recarga
      recargas.forEach(recarga => {
        // Calculate totals - handle multiple possible field names
        const precio = parseFloat(recarga.price) || parseFloat(recarga.amount) || parseFloat(recarga.valor) || 0;
        const ticketsVendidos = parseInt(recarga.ticketsAdded) || parseInt(recarga.tickets) || 0;
        
        report.totalIngresos += precio;
        report.totalTicketsVendidos += ticketsVendidos;

        // Count by vehicle type - handle multiple possible values
        const vehicleType = recarga.vehicleType || recarga.tipoVehiculo || '';
        if (vehicleType === 'auto' || vehicleType === 'Automovil' || vehicleType === 'Automóvil') {
          report.ticketsAutoVendidos += ticketsVendidos;
          report.recargasAuto++;
        } else if (vehicleType === 'moto' || vehicleType === 'Motocicleta') {
          report.ticketsMotoVendidos += ticketsVendidos;
          report.recargasMoto++;
        }

        // Group by date - handle multiple date field formats
        let fecha = 'Sin fecha';
        if (recarga.date) {
          fecha = formatTimestamp(recarga.date).split(',')[0];
        } else if (recarga.createdAt) {
          fecha = recarga.createdAt.split(',')[0];
        } else if (recarga.fecha) {
          fecha = recarga.fecha.split(',')[0];
        }
        report.recargasPorDia[fecha] = (report.recargasPorDia[fecha] || 0) + 1;

        // Group by CA - handle multiple possible field names
        const ca = recarga.numberCa || recarga.ca || recarga.numeroCA || 'Sin CA';
        if (!report.recargasPorCA[ca]) {
          report.recargasPorCA[ca] = { count: 0, total: 0, tickets: 0 };
        }
        report.recargasPorCA[ca].count++;
        report.recargasPorCA[ca].total += precio;
        report.recargasPorCA[ca].tickets += ticketsVendidos;

        // Group by vendor - handle multiple possible field names
        const vendor = recarga.createdBy || recarga.adminEmail || recarga.vendedor || recarga.userName || 'Sin vendedor';
        if (!report.recargasPorVendedor[vendor]) {
          report.recargasPorVendedor[vendor] = { count: 0, total: 0, tickets: 0 };
        }
        report.recargasPorVendedor[vendor].count++;
        report.recargasPorVendedor[vendor].total += precio;
        report.recargasPorVendedor[vendor].tickets += ticketsVendidos;
      });

      // Calculate averages
      report.promedioPrecio = report.totalRecargas > 0 ? report.totalIngresos / report.totalRecargas : 0;

      // Get top performers
      report.topVendedores = Object.entries(report.recargasPorVendedor)
        .sort(([,a], [,b]) => b.total - a.total)
        .slice(0, 5)
        .map(([vendor, data]) => ({
          vendor,
          total: data.total,
          count: data.count,
          tickets: data.tickets
        }));

      report.topCAs = Object.entries(report.recargasPorCA)
        .sort(([,a], [,b]) => b.total - a.total)
        .slice(0, 5)
        .map(([ca, data]) => ({
          ca,
          total: data.total,
          count: data.count,
          tickets: data.tickets
        }));

      // Get recent recargas - sort by date properly
      report.recargasRecientes = recargas
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date.seconds * 1000) : 
                       a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.date ? new Date(b.date.seconds * 1000) : 
                       b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        })
        .slice(0, 10);

      console.log('📊 Reporte de ventas generado:', report);
      setSalesReport(report);
    } catch (error) {
      console.error('❌ Error generando reporte:', error);
    }
  };

  // Function to update available tickets
  const updateAvailableTickets = () => {
    const availableData = calculateAvailableTickets();
    setTicketsData(availableData);
    setLastUpdate(new Date());
  };

  // Function to load purchase history
  const loadPurchaseHistory = async () => {
    setPurchaseHistoryLoading(true);
    try {
      const historyRef = collection(db, 'ticketRequestsInternas');
      const querySnapshot = await getDocs(historyRef);
      
      const history = [];
      querySnapshot.forEach((doc) => {
        history.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by requestedAt descending (most recent first)
      history.sort((a, b) => {
        const dateA = a.requestedAt?.seconds ? new Date(a.requestedAt.seconds * 1000) : new Date(0);
        const dateB = b.requestedAt?.seconds ? new Date(b.requestedAt.seconds * 1000) : new Date(0);
        return dateB - dateA;
      });
      
      setPurchaseHistory(history);
      setFilteredPurchaseHistory(history); // Inicializar también el historial filtrado
      console.log('📊 Historial de compras cargado:', history.length, 'registros');
      console.log('📊 Primer registro de ejemplo:', history[0]);
      console.log('📅 Campo requestedAt del primer registro:', history[0]?.requestedAt);
    } catch (error) {
      console.error('❌ Error cargando historial de compras:', error);
      showError('Error', 'No se pudo cargar el historial de compras');
    } finally {
      setPurchaseHistoryLoading(false);
    }
  };

  // Function to filter purchase history by date
  const filterPurchaseHistoryByDate = () => {
    try {
      if (!purchaseDateFilter.startDate || !purchaseDateFilter.endDate) {
        setFilteredPurchaseHistory(purchaseHistory);
        return;
      }

      const startDate = new Date(purchaseDateFilter.startDate);
      const endDate = new Date(purchaseDateFilter.endDate);
      endDate.setHours(23, 59, 59); // Include the entire end date

      const filtered = purchaseHistory.filter(request => {
        let requestDate;
        
        // Handle different date field formats - prioritize requestedAt for ticketRequestsInternas
        if (request.requestedAt) {
          if (typeof request.requestedAt === 'object' && request.requestedAt.seconds) {
            requestDate = new Date(request.requestedAt.seconds * 1000);
          } else {
            requestDate = new Date(request.requestedAt);
          }
        } else if (request.fechaOperacion) {
          if (typeof request.fechaOperacion === 'object' && request.fechaOperacion.seconds) {
            requestDate = new Date(request.fechaOperacion.seconds * 1000);
          } else {
            requestDate = new Date(request.fechaOperacion);
          }
        } else if (request.createdAt) {
          if (typeof request.createdAt === 'object' && request.createdAt.seconds) {
            requestDate = new Date(request.createdAt.seconds * 1000);
          } else {
            requestDate = new Date(request.createdAt);
          }
        } else if (request.fecha) {
          requestDate = new Date(request.fecha);
        } else {
          return false; // Skip if no date available
        }

        return requestDate >= startDate && requestDate <= endDate;
      });

      setFilteredPurchaseHistory(filtered);
      console.log(`📊 Historial de compras filtrado: ${filtered.length} solicitudes`);
      console.log(`📅 Filtro aplicado: desde ${purchaseDateFilter.startDate} hasta ${purchaseDateFilter.endDate}`);
    } catch (error) {
      console.error('❌ Error filtrando historial de compras:', error);
      setFilteredPurchaseHistory(purchaseHistory);
    }
  };

  // Function to clear purchase date filter
  const clearPurchaseDateFilter = () => {
    setPurchaseDateFilter({ startDate: '', endDate: '' });
    setFilteredPurchaseHistory(purchaseHistory);
  };

  // Auth effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verificar si el perfil del usuario existe en Firestore
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          const userData = userDoc.data();
          setUser(currentUser);
          setUserRole(userData.rol || 'Usuario');
          
          // Verificar si tiene permisos de administrador
          if (userData.rol !== 'Admin') {
            showError("Acceso denegado", "Solo administradores pueden acceder a Tickets Consorcio");
            navigate('/dashboard');
            return;
          }
          
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch data when component mounts
  useEffect(() => {
    if (user && userRole === 'Admin') {
      fetchRecargasData();
      loadPurchaseHistory(); // Cargar también el historial de compras
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Ejecutar solo cuando cambie user/userRole
  }, [user, userRole]);

  // En desarrollo: balance en tiempo real directo desde colecciones (sin resumen)
  useEffect(() => {
    if (!user || userRole !== 'Admin' || process.env.NODE_ENV !== 'development') return;

    let purchasedMoto = 0, purchasedAuto = 0, soldMoto = 0, soldAuto = 0;
    let histReady = false, rechReady = false;

    const recalcular = () => {
      if (!histReady || !rechReady) return;
      const availableMoto = Math.max(0, purchasedMoto - soldMoto);
      const availableAuto = Math.max(0, purchasedAuto - soldAuto);
      const now = new Date();
      setTicketsData({
        cantidadMoto:             availableMoto,
        cantidadAuto:             availableAuto,
        totalMotosCompradas:      purchasedMoto,
        totalVehiculosComprados:  purchasedAuto,
        totalMotosVendidas:       soldMoto,
        totalVehiculosVendidos:   soldAuto,
        updatedAt:                now,
        ultimaActualizacion:      now.toLocaleString('es-ES'),
      });
      setLastUpdate(now);
    };

    const unsubHistory = onSnapshot(collection(db, 'ticketRequestsInternas'), (snap) => {
      purchasedMoto = 0; purchasedAuto = 0;
      snap.forEach((d) => {
        purchasedMoto += parseInt(d.data().ticketsMotos)    || 0;
        purchasedAuto += parseInt(d.data().ticketsVehiculos) || 0;
      });
      histReady = true; recalcular();
    });

    const unsubRecharge = onSnapshot(collection(db, 'RechargeInternas'), (snap) => {
      soldMoto = 0; soldAuto = 0;
      snap.forEach((d) => {
        const data = d.data();
        const t = parseInt(data.ticketsAdded) || 0;
        if (data.vehicleType === 'moto') soldMoto += t; else soldAuto += t;
      });
      rechReady = true; recalcular();
    });

    return () => { unsubHistory(); unsubRecharge(); };
  }, [user, userRole]);

  // Update available tickets when both data sources are available
  useEffect(() => {
    if (purchaseHistory.length > 0 && salesReport) {
      updateAvailableTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseHistory, salesReport]);

  // Pagination functions
  const getPaginatedData = () => {
    if (!recargasData) return [];
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return recargasData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil((recargasData?.length || 0) / itemsPerPage);

  // Export to Excel function
  const exportToExcel = () => {
    if (!recargasData || recargasData.length === 0) {
      showError('Error', 'No hay datos para exportar');
      return;
    }

    try {
      // Import XLSX dynamically
      import('xlsx').then((XLSX) => {
        const exportData = recargasData.map(recarga => {
          const exportRow = {};
          
          if (selectedExportFields.id) {
            exportRow['ID'] = recarga.id || '';
          }
          if (selectedExportFields.fechaOperacion) {
            exportRow['Fecha Operación'] = formatTimestamp(recarga.date || recarga.createdAt || recarga.fechaOperacion);
          }
          if (selectedExportFields.amount) {
            exportRow['Amount'] = recarga.amount || 0;
          }
          if (selectedExportFields.description) {
            exportRow['Descripción'] = recarga.description || '';
          }
          if (selectedExportFields.executedBy) {
            exportRow['Ejecutado Por'] = recarga.executedBy || '';
          }
          if (selectedExportFields.newTicketsAuto) {
            exportRow['Nuevos Tickets Auto'] = recarga.newTicketsAuto || 0;
          }
          if (selectedExportFields.numberCa) {
            exportRow['Número CA'] = recarga.numberCa || '';
          }
          if (selectedExportFields.paymentMethod) {
            exportRow['Método de Pago'] = recarga.paymentMethod || '';
          }
          if (selectedExportFields.price) {
            exportRow['Precio'] = recarga.price || 0;
          }
          if (selectedExportFields.status) {
            exportRow['Estado'] = recarga.status || '';
          }
          if (selectedExportFields.telefono) {
            exportRow['Teléfono'] = recarga.telefono || '';
          }
          if (selectedExportFields.ticketsAdded) {
            exportRow['Tickets Agregados'] = recarga.ticketsAdded || 0;
          }
          if (selectedExportFields.userName) {
            exportRow['Nombre Usuario'] = recarga.userName || '';
          }
          if (selectedExportFields.vehicleType) {
            const vehicleType = recarga.vehicleType || '';
            exportRow['Tipo de Vehículo'] = vehicleType === 'auto' ? 'Auto' : 
                                           vehicleType === 'moto' ? 'Moto' : vehicleType;
          }
          if (selectedExportFields.token) {
            exportRow['Token'] = recarga.tokenticked || recarga.tokenTicked || recarga.token || 'N/A';
          }
          
          return exportRow;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recargas');
        
        const fileName = `recargas_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        
        showSuccess('Éxito', 'Reporte exportado correctamente');
        setShowExportModal(false);
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      showError('Error', 'No se pudo exportar el reporte');
    }
  };

  // Export to PDF function
  const exportToPDF = () => {
    // Usar filteredPurchaseHistory en lugar de purchaseHistory para solo exportar datos filtrados
    const dataToExport = filteredPurchaseHistory.length > 0 ? filteredPurchaseHistory : purchaseHistory;
    
    if (!dataToExport || dataToExport.length === 0) {
      showError('Error', 'No hay datos para exportar');
      return;
    }

    try {
      // Import jsPDF dynamically
      import('jspdf').then(({ default: jsPDF }) => {
        import('jspdf-autotable').then(({ default: autoTable }) => {
          const doc = new jsPDF();
          
          // Add title
          doc.setFontSize(20);
          doc.text('Historial de Compras - Solicitudes de Tickets', 20, 20);
          
          // Add date
          doc.setFontSize(12);
          doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 20, 30);
          
          // Add filter info if filters are applied
          if (purchaseDateFilter.startDate && purchaseDateFilter.endDate) {
            doc.setFontSize(10);
            doc.text(`Filtrado por fecha: ${purchaseDateFilter.startDate} a ${purchaseDateFilter.endDate}`, 20, 40);
          }
          
          // Add summary using filtered data
          const totalSolicitudes = dataToExport.length;
          const totalTickets = dataToExport.reduce((total, request) => total + (request.cantidadTickets || 0), 0);
          const totalMotos = dataToExport.reduce((total, request) => total + (request.ticketsMotos || 0), 0);
          const totalVehiculos = dataToExport.reduce((total, request) => total + (request.ticketsVehiculos || 0), 0);
          const valorMotos = totalMotos * 0.25;
          const valorVehiculos = totalVehiculos * 0.40;
          const valorTotal = valorMotos + valorVehiculos;
          
          doc.setFontSize(14);
          doc.text('Resumen:', 20, 50);
          doc.setFontSize(10);
          doc.text(`Total Solicitudes: ${totalSolicitudes}`, 20, 60);
          doc.text(`Total Tickets: ${totalTickets.toLocaleString()}`, 20, 70);
          doc.text(`Tickets Motos: ${totalMotos.toLocaleString()}`, 20, 80);
          doc.text(`Tickets Vehículos: ${totalVehiculos.toLocaleString()}`, 20, 90);
          doc.text(`Valor Total: $${valorTotal.toFixed(2)}`, 20, 100);
          
          // Prepare table data using filtered data
          const tableData = dataToExport.map((request, index) => [
            index + 1,
            formatTimestamp(request.requestedAt),
            request.requestedBy,
            request.cantidadTickets?.toLocaleString() || '0',
            request.ticketsMotos?.toLocaleString() || '0',
            request.ticketsVehiculos?.toLocaleString() || '0',
            `${request.porcentajeMotos}% / ${request.porcentajeVehiculos}%`,
            request.status || 'registrado',
            request.notes || ''
          ]);
          
          // Add table
          autoTable(doc, {
            head: [['#', 'Fecha', 'Solicitado Por', 'Total', 'Motos', 'Vehículos', 'Porcentajes', 'Estado', 'Notas']],
            body: tableData,
            startY: 115,
            styles: {
              fontSize: 8,
              cellPadding: 2
            },
            headStyles: {
              fillColor: [75, 0, 130],
              textColor: 255
            }
          });
          
          // Save PDF with filter info in filename
          let fileName = `historial_compras_${new Date().toISOString().split('T')[0]}`;
          if (purchaseDateFilter.startDate && purchaseDateFilter.endDate) {
            fileName += `_filtrado_${purchaseDateFilter.startDate}_a_${purchaseDateFilter.endDate}`;
          }
          fileName += '.pdf';
          
          doc.save(fileName);
          
          showSuccess('Éxito', `PDF generado correctamente con ${dataToExport.length} solicitudes`);
        });
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      showError('Error', 'No se pudo generar el PDF');
    }
  };

  // Export purchase history to Excel function
  const exportPurchaseHistoryToExcel = () => {
    // Usar filteredPurchaseHistory en lugar de purchaseHistory para solo exportar datos filtrados
    const dataToExport = filteredPurchaseHistory.length > 0 ? filteredPurchaseHistory : purchaseHistory;
    
    if (!dataToExport || dataToExport.length === 0) {
      showError('Error', 'No hay datos para exportar');
      return;
    }

    try {
      // Import XLSX dynamically
      import('xlsx').then((XLSX) => {
        const exportData = dataToExport.map((request, index) => ({
          '#': index + 1,
          'ID': request.id || '',
          'Fecha Solicitud': formatTimestamp(request.requestedAt),
          'Solicitado Por': request.requestedBy || '',
          'Cantidad Total': request.cantidadTickets?.toLocaleString() || '0',
          'Tickets Motos': request.ticketsMotos?.toLocaleString() || '0',
          'Tickets Vehículos': request.ticketsVehiculos?.toLocaleString() || '0',
          'Porcentaje Motos': request.porcentajeMotos || '0',
          'Porcentaje Vehículos': request.porcentajeVehiculos || '0',
          'Estado': request.status || 'registrado',
          'Notas': request.notes || '',
          'Valor Motos': `$${((request.ticketsMotos || 0) * 0.25).toFixed(2)}`,
          'Valor Vehículos': `$${((request.ticketsVehiculos || 0) * 0.40).toFixed(2)}`,
          'Valor Total': `$${(((request.ticketsMotos || 0) * 0.25) + ((request.ticketsVehiculos || 0) * 0.40)).toFixed(2)}`
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Historial Compras');
        
        // Save Excel with filter info in filename
        let fileName = `historial_compras_${new Date().toISOString().split('T')[0]}`;
        if (purchaseDateFilter.startDate && purchaseDateFilter.endDate) {
          fileName += `_filtrado_${purchaseDateFilter.startDate}_a_${purchaseDateFilter.endDate}`;
        }
        fileName += '.xlsx';
        
        XLSX.writeFile(wb, fileName);
        
        showSuccess('Éxito', `Excel exportado correctamente con ${dataToExport.length} solicitudes`);
      });
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      showError('Error', 'No se pudo exportar el Excel');
    }
  };

  // Export general report to PDF function
  const exportGeneralReportToPDF = () => {
    try {
      // Import jsPDF dynamically
      import('jspdf').then(({ default: jsPDF }) => {
        const doc = new jsPDF('landscape'); // Modo horizontal
        
        // Configurar colores
        const primaryColor = [59, 130, 246]; // Azul
        
        // Add title with background
          doc.setFillColor(...primaryColor);
          doc.rect(10, 10, 277, 20, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(24);
          doc.setFont(undefined, 'bold');
          doc.text('INFORME GENERAL DEL SISTEMA', 20, 25);
          
          // Add date
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES')}`, 20, 35);
          
          // Reset text color
          doc.setTextColor(0, 0, 0);
          
          // Section 1: Estado del Sistema (Top Left)
          doc.setFillColor(240, 249, 255);
          doc.rect(10, 40, 135, 80, 'F');
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text('ESTADO DEL SISTEMA', 20, 55);
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text(`Tickets Motocicletas: ${displayTicketsData?.cantidadMoto?.toLocaleString() || 0}`, 20, 70);
          doc.text(`Tickets Automóviles: ${displayTicketsData?.cantidadAuto?.toLocaleString() || 0}`, 20, 85);
          doc.text(`Comprados Motos: ${displayTicketsData?.totalMotosCompradas?.toLocaleString() || 0}`, 20, 100);
          doc.text(`Vendidos Motos: ${displayTicketsData?.totalMotosVendidas?.toLocaleString() || 0}`, 20, 115);
          
          // Section 2: Reporte de Ventas (Top Right)
          doc.setFillColor(240, 255, 244);
          doc.rect(155, 40, 135, 80, 'F');
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text('REPORTE DE VENTAS', 165, 55);
          
          if (salesReport) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text(`Total Recargas: ${salesReport.totalRecargas}`, 165, 70);
            doc.text(`Total Ingresos: $${salesReport.totalIngresos.toFixed(2)}`, 165, 85);
            doc.text(`Tickets Vendidos: ${salesReport.totalTicketsVendidos.toLocaleString()}`, 165, 100);
            doc.text(`Motos: ${salesReport.ticketsMotoVendidos.toLocaleString()} | Autos: ${salesReport.ticketsAutoVendidos.toLocaleString()}`, 165, 115);
          } else {
            doc.setFontSize(12);
            doc.text('No hay datos disponibles', 165, 85);
          }
          
          // Section 3: Reporte de Compras (Bottom Left)
          doc.setFillColor(255, 248, 240);
          doc.rect(10, 130, 135, 80, 'F');
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text('REPORTE DE COMPRAS', 20, 145);
          
          if (purchaseHistory && purchaseHistory.length > 0) {
            const totalSolicitudes = purchaseHistory.length;
            const totalTickets = purchaseHistory.reduce((total, request) => total + (request.cantidadTickets || 0), 0);
            const totalMotos = purchaseHistory.reduce((total, request) => total + (request.ticketsMotos || 0), 0);
            const totalVehiculos = purchaseHistory.reduce((total, request) => total + (request.ticketsVehiculos || 0), 0);
            const valorMotos = totalMotos * 0.25;
            const valorVehiculos = totalVehiculos * 0.40;
            const valorTotal = valorMotos + valorVehiculos;
            
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text(`Total Solicitudes: ${totalSolicitudes}`, 20, 160);
            doc.text(`Total Tickets: ${totalTickets.toLocaleString()}`, 20, 175);
            doc.text(`Motos: ${totalMotos.toLocaleString()} | Vehículos: ${totalVehiculos.toLocaleString()}`, 20, 190);
            doc.text(`Valor Total: $${valorTotal.toFixed(2)}`, 20, 205);
          } else {
            doc.setFontSize(12);
            doc.text('No hay solicitudes registradas', 20, 175);
          }
          
          // Section 4: Resumen Ejecutivo (Bottom Right)
          doc.setFillColor(255, 255, 240);
          doc.rect(155, 130, 135, 80, 'F');
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text('RESUMEN EJECUTIVO', 165, 145);
        
        // Calcular métricas generales
        const ticketsDisponibles = (displayTicketsData?.cantidadMoto || 0) + (displayTicketsData?.cantidadAuto || 0);
        const ticketsVendidos = salesReport ? salesReport.totalTicketsVendidos : 0;
        const ticketsComprados = (displayTicketsData?.totalMotosCompradas || 0) + (displayTicketsData?.totalVehiculosComprados || 0);
        const ingresosTotales = salesReport ? salesReport.totalIngresos : 0;
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        doc.text(`Tickets Disponibles: ${ticketsDisponibles.toLocaleString()}`, 165, 160);
        doc.text(`Tickets Comprados: ${ticketsComprados.toLocaleString()}`, 165, 175);
        doc.text(`Tickets Vendidos: ${ticketsVendidos.toLocaleString()}`, 165, 190);
        doc.text(`Ingresos Totales: $${ingresosTotales.toFixed(2)}`, 165, 205);
        
        // Footer
        doc.setFillColor(240, 240, 240);
        doc.rect(10, 220, 280, 15, 'F');
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('Sistema de Gestión de Tickets - Informe Generado Automáticamente', 20, 230);
        
        // Save PDF
        const fileName = `informe_general_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        
        showSuccess('Éxito', 'Informe general generado correctamente');
      });
    } catch (error) {
      console.error('Error al generar informe PDF:', error);
      showError('Error', 'No se pudo generar el informe PDF');
    }
  };

  // Function to save ticket request to history
  const saveToHistory = async () => {
    if (!historyAmount || historyAmount <= 0) {
      showError('Error', 'Por favor ingresa una cantidad válida de tickets');
      return;
    }

    // Validar que los porcentajes sumen 100%
    const totalPorcentaje = parseInt(historyPorcentajeMotos) + parseInt(historyPorcentajeVehiculos);
    if (totalPorcentaje !== 100) {
      showError('Error', `Los porcentajes deben sumar 100%. Actual: ${totalPorcentaje}%`);
      return;
    }

    setHistoryLoading(true);
    
    try {
      // Crear documento en la colección de historial
      const historyRef = collection(db, 'ticketRequestsInternas');
      const historyData = {
        cantidadTickets: parseInt(historyAmount),
        porcentajeMotos: historyPorcentajeMotos,
        porcentajeVehiculos: historyPorcentajeVehiculos,
        ticketsMotos: Math.round((parseInt(historyAmount) * parseInt(historyPorcentajeMotos)) / 100),
        ticketsVehiculos: Math.round((parseInt(historyAmount) * parseInt(historyPorcentajeVehiculos)) / 100),
        requestedBy: user?.email || 'Sistema',
        requestedAt: new Date(),
        status: 'registrado',
        notes: `Solicitud registrada por ${user?.email || 'Sistema'}`
      };

      await addDoc(historyRef, historyData);

      // Generar PDF de la solicitud
      try {
        import('jspdf').then(({ default: jsPDF }) => {
          const doc = new jsPDF();
          
          // Configurar colores
          const primaryColor = [59, 130, 246]; // Azul
          
          // Add title with background
          doc.setFillColor(...primaryColor);
          doc.rect(10, 10, 190, 20, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          doc.text('CONSORCIO PARQUEO AMIGO', 20, 25);
          
          // Add subtitle
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text('SOLICITUD DE TICKETS - SISTEMA CONFIABLE', 20, 35);
          
          // Add date
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES')}`, 20, 45);
          
          // Reset text color
          doc.setTextColor(0, 0, 0);
          
          // Section 1: Detalles de la Solicitud
          doc.setFillColor(240, 249, 255);
          doc.rect(10, 50, 190, 60, 'F');
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('DETALLES DE LA SOLICITUD', 20, 65);
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text(`Solicitado por: ${user?.email || 'Sistema'}`, 20, 80);
          doc.text(`Cantidad Total: ${parseInt(historyAmount).toLocaleString()} tickets`, 20, 95);
          doc.text(`Fecha de Solicitud: ${new Date().toLocaleDateString('es-ES')}`, 20, 110);
          
          // Section 2: Distribución de Tickets
          doc.setFillColor(240, 255, 244);
          doc.rect(10, 120, 190, 50, 'F');
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('DISTRIBUCIÓN DE TICKETS', 20, 135);
          
          const ticketsMotos = Math.round((parseInt(historyAmount) * parseInt(historyPorcentajeMotos)) / 100);
          const ticketsVehiculos = Math.round((parseInt(historyAmount) * parseInt(historyPorcentajeVehiculos)) / 100);
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text(`Motocicletas: ${ticketsMotos.toLocaleString()} tickets (${historyPorcentajeMotos}%)`, 20, 150);
          doc.text(`Vehículos: ${ticketsVehiculos.toLocaleString()} tickets (${historyPorcentajeVehiculos}%)`, 20, 165);
          
          // Section 3: Valor Monetario
          doc.setFillColor(255, 248, 240);
          doc.rect(10, 180, 190, 40, 'F');
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('VALOR MONETARIO', 20, 195);
          
          const valorMotos = ticketsMotos * 0.25;
          const valorVehiculos = ticketsVehiculos * 0.40;
          const valorTotal = valorMotos + valorVehiculos;
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text(`Valor Motos: $${valorMotos.toFixed(2)}`, 20, 210);
          doc.text(`Valor Vehículos: $${valorVehiculos.toFixed(2)}`, 20, 225);
          doc.setFont(undefined, 'bold');
          doc.text(`Valor Total: $${valorTotal.toFixed(2)}`, 20, 240);
          
          // Section 4: Garantía de Calidad
          doc.setFillColor(255, 255, 240);
          doc.rect(10, 230, 190, 30, 'F');
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('GARANTÍA DE CALIDAD', 20, 245);
          
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text('Sistema 100% confiable y auditado', 20, 255);
          doc.text('Datos verificados y seguros', 20, 265);
          
          // Footer
          doc.setFillColor(240, 240, 240);
          doc.rect(10, 270, 190, 15, 'F');
          doc.setTextColor(100, 100, 100);
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text('CONSORCIO PARQUEO AMIGO - Sistema Confiable', 20, 280);
          
          // Save PDF
          const fileName = `solicitud_tickets_${new Date().toISOString().split('T')[0]}_${Date.now()}.pdf`;
          doc.save(fileName);
        });
      } catch (pdfError) {
        console.error('Error al generar PDF:', pdfError);
        // No mostrar error al usuario, solo log
      }
      
      showSuccess('Éxito', 'Solicitud guardada en el historial correctamente');
      setShowHistoryModal(false);
      setHistoryAmount('');
      setHistoryPorcentajeMotos('45');
      setHistoryPorcentajeVehiculos('55');
      
      console.log('✅ Solicitud guardada en historial:', historyData);
    } catch (error) {
      console.error('❌ Error al guardar en historial:', error);
      showError('Error', `No se pudo guardar la solicitud: ${error.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Genera id y tokens únicos basados en fecha/hora Ecuador (auto=1, moto=3)
  const generateTicketRequestIdAndTokens = () => {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Guayaquil',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
    const parts = fmt.formatToParts(now);
    const get = (t) => parts.find(p => p.type === t)?.value || '00';
    const yyyy = get('year');
    const mm = get('month');
    const dd = get('day');
    const hh = get('hour');
    const min = get('minute');
    const ss = get('second');
    const extra = String(now.getMilliseconds()).padStart(3, '0').slice(0, 2);
    const id = `${yyyy}${mm}${dd}${hh}${min}${ss}${extra}`;
    const tokenSuffix = String(Math.floor(Math.random() * 10));
    const tokenAuto = `1${yyyy}${mm}${dd}${hh}${min}${ss}${tokenSuffix}`;
    const tokenMoto = `3${yyyy}${mm}${dd}${hh}${min}${ss}${tokenSuffix}`;
    return { id, tokenAuto, tokenMoto };
  };

  // Function to create internal ticket request (saved to ticketRequestsInternas)
  const requestTickets = async () => {
    if (!requestAmount || requestAmount <= 0) {
      showError('Error', 'Por favor ingresa una cantidad válida de tickets');
      return;
    }

    // Validar que los porcentajes sumen 100%
    const totalPorcentaje = parseInt(porcentajeMotos) + parseInt(porcentajeVehiculos);
    if (totalPorcentaje !== 100) {
      showError('Error', `Los porcentajes deben sumar 100%. Actual: ${totalPorcentaje}%`);
      return;
    }

    // Validar contraseña
    if (!requestPassword || requestPassword.trim() === '') {
      showError('Error', 'Por favor ingresa tu contraseña para confirmar la solicitud');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      showError('Error', 'No hay usuario autenticado. Inicia sesión nuevamente.');
      return;
    }
    const isEmailProvider = currentUser.providerData?.some(p => p.providerId === 'password');
    if (!isEmailProvider) {
      showError('Error', 'Esta acción requiere una cuenta con email y contraseña. Tu cuenta usa otro método de inicio de sesión.');
      return;
    }

    setRequestLoading(true);

    try {
      // Verificar contraseña mediante re-autenticación en Firebase
      const credential = EmailAuthProvider.credential(currentUser.email, requestPassword.trim());
      await reauthenticateWithCredential(currentUser, credential);
    } catch (authError) {
      setRequestLoading(false);
      if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
        showError('Contraseña incorrecta', 'La contraseña no es correcta. Verifica e intenta de nuevo.');
      } else if (authError.code === 'auth/too-many-requests') {
        showError('Error', 'Demasiados intentos fallidos. Espera un momento antes de intentar de nuevo.');
      } else {
        showError('Error de autenticación', authError.message || 'No se pudo verificar la contraseña');
      }
      return;
    }
    
    try {
      const cantidadTicketsInt = parseInt(requestAmount);
      const ticketsMotos = Math.round((cantidadTicketsInt * parseInt(porcentajeMotos)) / 100);
      const ticketsVehiculos = Math.round((cantidadTicketsInt * parseInt(porcentajeVehiculos)) / 100);
      const cantidadAuto = ticketsVehiculos;
      const cantidadMoto = ticketsMotos;

      const { id, tokenAuto, tokenMoto } = generateTicketRequestIdAndTokens();
      const fechaEcuador = `${id.slice(0, 4)}-${id.slice(4, 6)}-${id.slice(6, 8)}`;
      const timestampEcuador = `${fechaEcuador}T${id.slice(8, 10)}:${id.slice(10, 12)}:${id.slice(12, 14)}.${id.slice(14)}0Z`;
      const requestedBy = user?.email || 'system';

      const solicitudData = {
        id,
        cantidadAuto,
        cantidadMoto,
        cantidadTickets: cantidadTicketsInt,
        createdAt: serverTimestamp(),
        fechaEcuador,
        notes: `Solicitud registrada por ${requestedBy}`,
        porcentajeMotos: String(porcentajeMotos),
        porcentajeVehiculos: String(porcentajeVehiculos),
        requestedAt: serverTimestamp(),
        requestedBy,
        status: 'pendiente',
        ticketsConsumidosAuto: 0,
        ticketsConsumidosMoto: 0,
        ticketsMotos,
        ticketsVehiculos,
        timestampEcuador,
        tokenAuto,
        tokenMoto,
        ultimaActualizacion: fechaEcuador,
        updatedAt: serverTimestamp(),
        zonaHoraria: 'America/Guayaquil (GMT-5)'
      };

      const docRef = doc(db, 'ticketRequestsInternas', id);
      await setDoc(docRef, solicitudData);

      const successMessage = `Solicitud interna registrada correctamente: ${requestAmount} tickets (${porcentajeMotos}% motos, ${porcentajeVehiculos}% vehículos)`;

      // Generar PDF de la solicitud
      try {
          import('jspdf').then(({ default: jsPDF }) => {
            const doc = new jsPDF();
            
            // Configurar colores
            const primaryColor = [59, 130, 246]; // Azul
            
            // Add title with background
            doc.setFillColor(...primaryColor);
            doc.rect(10, 10, 190, 20, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.setFont(undefined, 'bold');
            doc.text('SOLICITUD DE TICKETS', 20, 25);
            
            // Add date
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES')}`, 20, 35);
            
            // Reset text color
            doc.setTextColor(0, 0, 0);
            
            // Section 1: Detalles de la Solicitud
            doc.setFillColor(240, 249, 255);
            doc.rect(10, 40, 190, 60, 'F');
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('DETALLES DE LA SOLICITUD', 20, 55);
            
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text(`Solicitado por: ${user?.email || 'Sistema'}`, 20, 70);
            doc.text(`Cantidad Total: ${parseInt(requestAmount).toLocaleString()} tickets`, 20, 85);
            doc.text(`Fecha de Solicitud: ${new Date().toLocaleDateString('es-ES')}`, 20, 100);
            
            // Section 2: Distribución de Tickets
            doc.setFillColor(240, 255, 244);
            doc.rect(10, 110, 190, 50, 'F');
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('DISTRIBUCIÓN DE TICKETS', 20, 125);
            
            const ticketsMotos = Math.round((parseInt(requestAmount) * parseInt(porcentajeMotos)) / 100);
            const ticketsVehiculos = Math.round((parseInt(requestAmount) * parseInt(porcentajeVehiculos)) / 100);
            
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text(`Motocicletas: ${ticketsMotos.toLocaleString()} tickets (${porcentajeMotos}%)`, 20, 140);
            doc.text(`Vehículos: ${ticketsVehiculos.toLocaleString()} tickets (${porcentajeVehiculos}%)`, 20, 155);
            
            // Section 3: Valor Monetario
            doc.setFillColor(255, 248, 240);
            doc.rect(10, 170, 190, 40, 'F');
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('VALOR MONETARIO', 20, 185);
            
            const valorMotos = ticketsMotos * 0.25;
            const valorVehiculos = ticketsVehiculos * 0.40;
            const valorTotal = valorMotos + valorVehiculos;
            
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text(`Valor Motos: $${valorMotos.toFixed(2)}`, 20, 200);
            doc.text(`Valor Vehículos: $${valorVehiculos.toFixed(2)}`, 20, 215);
            doc.setFont(undefined, 'bold');
            doc.text(`Valor Total: $${valorTotal.toFixed(2)}`, 20, 230);
            
            // Section 4: Estado de la Solicitud
            doc.setFillColor(255, 255, 240);
            doc.rect(10, 220, 190, 30, 'F');
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('ESTADO DE LA SOLICITUD', 20, 235);
            
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text(`Estado: Pendiente (esperando aprobación)`, 20, 250);
            doc.text(`ID: ${id} | Token Auto: ${tokenAuto} | Token Moto: ${tokenMoto}`, 20, 265);
            
            // Footer
            doc.setFillColor(240, 240, 240);
            doc.rect(10, 270, 190, 15, 'F');
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text('Sistema de Gestión de Tickets - Solicitud Procesada', 20, 280);
            
            // Save PDF
            const fileName = `solicitud_tickets_${new Date().toISOString().split('T')[0]}_${Date.now()}.pdf`;
            doc.save(fileName);
          });
      } catch (pdfError) {
        console.error('Error al generar PDF:', pdfError);
        // No mostrar error al usuario, solo log
      }
      
      showSuccess('Éxito', successMessage);
      setShowRequestModal(false);
      setRequestAmount('');
      setPorcentajeMotos('45');
      setPorcentajeVehiculos('55');
      setRequestPassword('');
      console.log('✅ Solicitud de tickets exitosa, ID:', id);
    } catch (error) {
      console.error('❌ Error al solicitar tickets:', error);
      showError('Error', `No se pudo procesar la solicitud: ${error.message}`);
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Initialize ticketsData with default values if null
  const displayTicketsData = ticketsData || {
    cantidadMoto: 0,
    cantidadAuto: 0,
    totalMotosCompradas: 0,
    totalVehiculosComprados: 0,
    totalMotosVendidas: 0,
    totalVehiculosVendidos: 0,
    updatedAt: new Date(),
    ultimaActualizacion: new Date().toLocaleString('es-ES')
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Tickets Disponibles" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile to prevent content from being hidden under header */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <h1 className="text-xl font-bold text-gray-900">Tickets Disponibles</h1>
                <div className="ml-4 flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-2"></div>
                  <span className="text-sm text-gray-600">Tiempo Real</span>
                </div>
                {userRole === 'Admin' && (
                  <>
                  <button
                    onClick={() => setShowRequestModal(true)}
                    className="ml-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Solicitar Tickets
                  </button>
                    {process.env.NODE_ENV === 'development' && (
                      <>
                        <button
                          onClick={() => setShowHistoryModal(true)}
                          className="ml-3 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Registrar Solicitud
                        </button>
                        <button
                          onClick={() => {
                            setShowPurchaseHistory(true);
                            loadPurchaseHistory();
                          }}
                          className="ml-3 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Historial Compras
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">{userRole}</p>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">Estado del Sistema de Tickets</h2>
                  {lastUpdate && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Última actualización:</p>
                      <p className="text-sm font-medium text-green-600">{formatTimestamp(lastUpdate)}</p>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={`p-4 rounded-lg ${(displayTicketsData?.cantidadMoto ?? 0) < 0 ? 'bg-red-50' : 'bg-blue-50'}`}>
                    <div className="flex items-center mb-2">
                      <span className="text-3xl mr-3">🏍️</span>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Tickets Motocicletas</p>
                        <p className={`text-xs font-semibold ${(displayTicketsData?.cantidadMoto ?? 0) < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {(displayTicketsData?.cantidadMoto ?? 0) < 0 ? '⚠️ EN CONTRA' : 'Disponibles para venta'}
                        </p>
                      </div>
                    </div>
                    <p className={`text-4xl font-bold ${(displayTicketsData?.cantidadMoto ?? 0) > 0 ? 'text-blue-600' : (displayTicketsData?.cantidadMoto ?? 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {(displayTicketsData?.cantidadMoto ?? 0).toLocaleString()}
                    </p>
                    <div className="mt-2 bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600">Comprados: {displayTicketsData?.totalMotosCompradas?.toLocaleString() || 0}</p>
                      <p className="text-xs text-gray-600">Vendidos: {displayTicketsData?.totalMotosVendidas?.toLocaleString() || 0}</p>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg ${(displayTicketsData?.cantidadAuto ?? 0) < 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className="flex items-center mb-2">
                      <span className="text-3xl mr-3">🚗</span>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Tickets Automóviles</p>
                        <p className={`text-xs font-semibold ${(displayTicketsData?.cantidadAuto ?? 0) < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {(displayTicketsData?.cantidadAuto ?? 0) < 0 ? '⚠️ EN CONTRA' : 'Disponibles para venta'}
                        </p>
                      </div>
                    </div>
                    <p className={`text-4xl font-bold ${(displayTicketsData?.cantidadAuto ?? 0) > 0 ? 'text-green-600' : (displayTicketsData?.cantidadAuto ?? 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {(displayTicketsData?.cantidadAuto ?? 0).toLocaleString()}
                    </p>
                    <div className="mt-2 bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600">Comprados: {displayTicketsData?.totalVehiculosComprados?.toLocaleString() || 0}</p>
                      <p className="text-xs text-gray-600">Vendidos: {displayTicketsData?.totalVehiculosVendidos?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </div>
                
                {/* Summary of calculation */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">📊 Cálculo de Disponibilidad</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-gray-600">🏍️ Motocicletas:</p>
                      <p className={`font-medium ${(displayTicketsData?.cantidadMoto ?? 0) < 0 ? 'text-red-600' : ''}`}>
                        {displayTicketsData?.totalMotosCompradas?.toLocaleString() || 0} comprados - {displayTicketsData?.totalMotosVendidas?.toLocaleString() || 0} vendidos = {(displayTicketsData?.cantidadMoto ?? 0).toLocaleString()} {(displayTicketsData?.cantidadMoto ?? 0) < 0 ? '⚠️ en contra' : 'disponibles'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">🚗 Automóviles:</p>
                      <p className={`font-medium ${(displayTicketsData?.cantidadAuto ?? 0) < 0 ? 'text-red-600' : ''}`}>
                        {displayTicketsData?.totalVehiculosComprados?.toLocaleString() || 0} comprados - {displayTicketsData?.totalVehiculosVendidos?.toLocaleString() || 0} vendidos = {(displayTicketsData?.cantidadAuto ?? 0).toLocaleString()} {(displayTicketsData?.cantidadAuto ?? 0) < 0 ? '⚠️ en contra' : 'disponibles'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales and Purchase Reports Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Sales Report Section */}
            {salesReport && (
                <div className="bg-white rounded-lg shadow-md">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-800">📊 Reporte de Ventas</h3>
                      
                      {/* Date Filter */}
                      <div className="flex items-center space-x-4">
                        <div>
                          <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">
                            Desde
                          </label>
                          <input
                            type="date"
                            id="startDate"
                            value={dateFilter.startDate}
                            onChange={(e) => {
                              setDateFilter(prev => ({ ...prev, startDate: e.target.value }));
                            }}
                            className="px-3 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">
                            Hasta
                          </label>
                          <input
                            type="date"
                            id="endDate"
                            value={dateFilter.endDate}
                            onChange={(e) => {
                              setDateFilter(prev => ({ ...prev, endDate: e.target.value }));
                            }}
                            className="px-3 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <button
                          onClick={fetchRecargasData}
                          className="px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Filtrar
                        </button>
                        <button
                          onClick={() => {
                            setDateFilter({ startDate: '', endDate: '' });
                            fetchRecargasData();
                          }}
                          className="px-4 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                        >
                          Limpiar
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Recargas</p>
                      <p className="text-2xl font-bold text-blue-600">{salesReport.totalRecargas}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Ingresos</p>
                      <p className="text-2xl font-bold text-green-600">${salesReport.totalIngresos.toFixed(2)}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Tickets Vendidos</p>
                      <p className="text-2xl font-bold text-purple-600">{salesReport.totalTicketsVendidos.toLocaleString()}</p>
                    </div>
                  </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-3">Ventas por Tipo de Vehículo</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                          <span className="font-medium">🏍️ Motocicletas</span>
                          <span className="font-bold text-blue-600">{salesReport.ticketsMotoVendidos.toLocaleString()} tickets</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                          <span className="font-medium">🚗 Automóviles</span>
                          <span className="font-bold text-green-600">{salesReport.ticketsAutoVendidos.toLocaleString()} tickets</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Purchase Report Section */}
              <div className="bg-white rounded-lg shadow-md">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">📋 Reporte de Compras</h3>
                    
                    <button
                      onClick={() => {
                        setShowPurchaseHistory(true);
                        loadPurchaseHistory();
                      }}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Ver Historial
                    </button>
                  </div>
                  
                  {/* Filtros de fecha para Reporte de Compras */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Filtrar por fecha</h4>
                    <div className="flex flex-wrap items-center gap-3">
                      <div>
                        <label htmlFor="purchaseStartDate" className="block text-xs font-medium text-gray-700 mb-1">
                          Desde
                        </label>
                        <input
                          type="date"
                          id="purchaseStartDate"
                          value={purchaseDateFilter.startDate}
                          onChange={(e) => {
                            setPurchaseDateFilter(prev => ({ ...prev, startDate: e.target.value }));
                          }}
                          className="px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="purchaseEndDate" className="block text-xs font-medium text-gray-700 mb-1">
                          Hasta
                        </label>
                        <input
                          type="date"
                          id="purchaseEndDate"
                          value={purchaseDateFilter.endDate}
                          onChange={(e) => {
                            setPurchaseDateFilter(prev => ({ ...prev, endDate: e.target.value }));
                          }}
                          className="px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                                        <button
                    onClick={() => {
                      console.log('🔍 Botón Filtrar (Reporte) clickeado');
                      console.log('📅 Filtros actuales:', purchaseDateFilter);
                      console.log('📊 Historial total:', purchaseHistory.length);
                      filterPurchaseHistoryByDate();
                    }}
                    className="px-4 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors"
                  >
                    Filtrar
                  </button>
                      <button
                        onClick={clearPurchaseDateFilter}
                        className="px-4 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>

                  {purchaseHistoryLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Solicitudes</p>
                          <p className="text-2xl font-bold text-blue-600">{filteredPurchaseHistory.length}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Tickets</p>
                          <p className="text-2xl font-bold text-green-600">
                            {filteredPurchaseHistory.reduce((total, request) => total + (request.cantidadTickets || 0), 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Tickets Disponibles</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {((displayTicketsData?.cantidadMoto || 0) + (displayTicketsData?.cantidadAuto || 0)).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Distribución por Tipo</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">🏍️ Motos</span>
                            <span className="text-sm font-bold text-blue-600">
                              {filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsMotos || 0), 0).toLocaleString()} tickets
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">🚗 Vehículos</span>
                            <span className="text-sm font-bold text-green-600">
                              {filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsVehiculos || 0), 0).toLocaleString()} tickets
                            </span>
                          </div>
                        </div>
                      </div>
                      
                    
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">💰 Valor Monetario</p>
                        <div className="space-y-1">
                          {(() => {
                            const totalMotos = filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsMotos || 0), 0);
                            const totalVehiculos = filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsVehiculos || 0), 0);
                            const valorMotos = totalMotos * 0.25; // 25 centavos por ticket de moto
                            const valorVehiculos = totalVehiculos * 0.40; // 40 centavos por ticket de auto
                            const valorTotal = valorMotos + valorVehiculos;
                            
                            return (
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-gray-500">🏍️ Valor motos:</span>
                                  <span className="text-sm font-medium text-blue-600">
                                    ${valorMotos.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-gray-500">🚗 Valor vehículos:</span>
                                  <span className="text-sm font-medium text-green-600">
                                    ${valorVehiculos.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center border-t pt-1">
                                  <span className="text-xs font-medium text-gray-700">💰 Valor total:</span>
            
                                  <span className="text-sm font-bold text-orange-600">
                                    ${valorTotal.toFixed(2)}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Estado de Solicitudes</p>
                        <div className="space-y-1">
                          {(() => {
                            const statusCount = filteredPurchaseHistory.reduce((acc, request) => {
                              const status = request.status || 'registrado';
                              acc[status] = (acc[status] || 0) + 1;
                              return acc;
                            }, {});
                            
                            return Object.entries(statusCount).map(([status, count]) => (
                              <div key={status} className="flex justify-between items-center">
                                <span className="text-xs text-gray-500 capitalize">{status}:</span>
                                <span className="text-sm font-medium">{count}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-6">
                <div className="flex justify-between items-center  quiero filtral mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Información del Sistema</h3>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={updateAvailableTickets}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center shadow-md"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Recalcular
                    </button>
                    <button
                      onClick={exportGeneralReportToPDF}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center shadow-md"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Descargar Informe PDF
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Última actualización</p>
                    <p className="font-medium text-gray-900">{formatTimestamp(displayTicketsData?.updatedAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cálculo automático</p>
                    <p className="font-medium text-green-600">Disponibles = Comprados - Vendidos</p>
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">📊 Resumen de Movimientos</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 font-medium">🏍️ Motocicletas:</p>
                      <ul className="text-xs space-y-1 mt-1">
                        <li>• Comprados: {displayTicketsData?.totalMotosCompradas?.toLocaleString() || 0} tickets</li>
                        <li>• Vendidos: {displayTicketsData?.totalMotosVendidas?.toLocaleString() || 0} tickets</li>
                        <li>• Disponibles: {displayTicketsData?.cantidadMoto?.toLocaleString() || 0} tickets</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">🚗 Automóviles:</p>
                      <ul className="text-xs space-y-1 mt-1">
                        <li>• Comprados: {displayTicketsData?.totalVehiculosComprados?.toLocaleString() || 0} tickets</li>
                        <li>• Vendidos: {displayTicketsData?.totalVehiculosVendidos?.toLocaleString() || 0} tickets</li>
                        <li>• Disponibles: {displayTicketsData?.cantidadAuto?.toLocaleString() || 0} tickets</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>



            {/* Tabla Completa de Recargas */}
            {recargasData && recargasData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md mb-6">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">📋 Tabla Completa de Recargas</h3>
                    
                    {/* Export Button */}
                    <button
                      onClick={() => setShowExportModal(true)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Exportar Excel
                    </button>
                  </div>
                  
                  {/* Table Filters */}
                  <div className="mb-4 flex flex-wrap gap-4 items-center">
                  <div>
                      <label htmlFor="tableStartDate" className="block text-xs font-medium text-gray-700 mb-1">
                        Fecha Desde
                      </label>
                      <input
                        type="date"
                        id="tableStartDate"
                        value={dateFilter.startDate}
                        onChange={(e) => {
                          setDateFilter(prev => ({ ...prev, startDate: e.target.value }));
                          setCurrentPage(1); // Reset to first page
                        }}
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                      />
                  </div>
                  <div>
                      <label htmlFor="tableEndDate" className="block text-xs font-medium text-gray-700 mb-1">
                        Fecha Hasta
                      </label>
                      <input
                        type="date"
                        id="tableEndDate"
                        value={dateFilter.endDate}
                        onChange={(e) => {
                          setDateFilter(prev => ({ ...prev, endDate: e.target.value }));
                          setCurrentPage(1); // Reset to first page
                        }}
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                      />
                  </div>
                  <div>
                      <label htmlFor="itemsPerPage" className="block text-xs font-medium text-gray-700 mb-1">
                        Registros por página
                      </label>
                      <select
                        id="itemsPerPage"
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(parseInt(e.target.value));
                          setCurrentPage(1); // Reset to first page
                        }}
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                  </div>
                    <div className="flex items-end">
                      <button
                        onClick={fetchRecargasData}
                        className="px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Aplicar Filtros
                      </button>
                </div>
              </div>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      Mostrando {recargasData.length} registros de recargas
                      {dateFilter.startDate || dateFilter.endDate ? ' (filtrados)' : ''}
                    </p>
            </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CA</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Vehículo</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tickets</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getPaginatedData().map((recarga, index) => (
                          <tr key={recarga.id || index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {recarga.id ? recarga.id.substring(0, 8) + '...' : (currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatTimestamp(recarga.date || recarga.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {recarga.numberCa || recarga.ca || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {recarga.vehicleType === 'auto' || recarga.vehicleType === 'Automovil' ? '🚗 Auto' : 
                               recarga.vehicleType === 'moto' || recarga.vehicleType === 'Motocicleta' ? '🏍️ Moto' : 
                               recarga.vehicleType || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {recarga.ticketsAdded || recarga.tickets || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${(recarga.price || recarga.amount || recarga.valor || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {recarga.createdBy || recarga.adminEmail || recarga.vendedor || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                recarga.status === 'completado' || recarga.estado === 'completado' 
                                  ? 'bg-green-100 text-green-800' 
                                  : recarga.status === 'pendiente' || recarga.estado === 'pendiente'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {recarga.status || recarga.estado || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-mono font-medium bg-indigo-100 text-indigo-800">
                                {recarga.tokenticked || recarga.tokenTicked || recarga.token || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, recargasData.length)} de {recargasData.length} registros
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Anterior
                          </button>
                          
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-3 py-1 text-sm border rounded ${
                                  currentPage === pageNum
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Alerts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Alert for low motorcycle tickets */}
              {displayTicketsData?.cantidadMoto <= 1000 && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Stock Bajo - Motocicletas</h3>
                      <p className="mt-1 text-sm text-red-700">Solo quedan {displayTicketsData?.cantidadMoto} tickets para motocicletas. Considera solicitar más tickets.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alert for low auto tickets */}
              {displayTicketsData?.cantidadAuto <= 1000 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Stock Bajo - Automóviles</h3>
                      <p className="mt-1 text-sm text-yellow-700">Solo quedan {displayTicketsData?.cantidadAuto} tickets para automóviles. Considera solicitar más tickets.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success message when stock is good */}
              {displayTicketsData?.cantidadMoto > 1000 && displayTicketsData?.cantidadAuto > 1000 && (
                <div className="md:col-span-2 bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">Sistema Operativo</h3>
                      <p className="mt-1 text-sm text-green-700">Los stocks de tickets están en niveles adecuados para la operación normal.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Floating buttons for mobile - Solo para Admin */}
      {userRole === 'Admin' && (
        <>
        <button
          onClick={() => setShowRequestModal(true)}
          className="md:hidden fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg z-40 transition-all duration-200"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
          {process.env.NODE_ENV === 'development' && (
            <>
              <button
                onClick={() => setShowHistoryModal(true)}
                className="md:hidden fixed bottom-6 right-20 bg-green-600 hover:bg-green-700 text-white p-4 rounded-full shadow-lg z-40 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
              <button
                onClick={() => {
                  setShowPurchaseHistory(true);
                  loadPurchaseHistory();
                }}
                className="md:hidden fixed bottom-6 right-34 bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-full shadow-lg z-40 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </button>
            </>
          )}
        </>
      )}

      {/* Modal for export configuration */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Exportar a Excel</h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona los campos que quieres incluir en el reporte Excel:
                </p>
                
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {Object.entries(selectedExportFields).map(([field, isSelected]) => (
                    <label key={field} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          setSelectedExportFields(prev => ({
                            ...prev,
                            [field]: e.target.checked
                          }));
                        }}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {field === 'id' && 'ID'}
                        {field === 'fechaOperacion' && 'Fecha Operación'}
                        {field === 'amount' && 'Amount'}
                        {field === 'description' && 'Descripción'}
                        {field === 'executedBy' && 'Ejecutado Por'}
                        {field === 'newTicketsAuto' && 'Nuevos Tickets Auto'}
                        {field === 'numberCa' && 'Número CA'}
                        {field === 'paymentMethod' && 'Método de Pago'}
                        {field === 'price' && 'Precio'}
                        {field === 'status' && 'Estado'}
                        {field === 'telefono' && 'Teléfono'}
                        {field === 'ticketsAdded' && 'Tickets Agregados'}
                        {field === 'userName' && 'Nombre Usuario'}
                        {field === 'vehicleType' && 'Tipo de Vehículo'}
                        {field === 'token' && 'Token'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  Exportar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for purchase history */}
      {showPurchaseHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">📋 Historial de Compras - Solicitudes de Tickets</h3>
                
                {/* Export Buttons and Close */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => exportPurchaseHistoryToExcel()}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Exportar Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar PDF
                  </button>
                  <button
                    onClick={() => setShowPurchaseHistory(false)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Cerrar"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Filtros de fecha para el modal */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Filtrar por fecha</h4>
                <div className="flex flex-wrap items-center gap-3">
                  <div>
                    <label htmlFor="modalPurchaseStartDate" className="block text-xs font-medium text-gray-700 mb-1">
                      Desde
                    </label>
                    <input
                      type="date"
                      id="modalPurchaseStartDate"
                      value={purchaseDateFilter.startDate}
                      onChange={(e) => {
                        setPurchaseDateFilter(prev => ({ ...prev, startDate: e.target.value }));
                      }}
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="modalPurchaseEndDate" className="block text-xs font-medium text-gray-700 mb-1">
                      Hasta
                    </label>
                    <input
                      type="date"
                      id="modalPurchaseEndDate"
                      value={purchaseDateFilter.endDate}
                      onChange={(e) => {
                        setPurchaseDateFilter(prev => ({ ...prev, endDate: e.target.value }));
                      }}
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      console.log('🔍 Botón Filtrar clickeado');
                      console.log('📅 Filtros actuales:', purchaseDateFilter);
                      console.log('📊 Historial total:', purchaseHistory.length);
                      filterPurchaseHistoryByDate();
                    }}
                    className="px-4 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors"
                  >
                    Filtrar
                  </button>
                  <button
                    onClick={clearPurchaseDateFilter}
                    className="px-4 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              {/* Purchase History Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">📊 Resumen de Solicitudes</h4>
                  
                  {purchaseHistoryLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Total Solicitudes</p>
                          <p className="text-2xl font-bold text-blue-600">{filteredPurchaseHistory.length}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Total Tickets</p>
                          <p className="text-2xl font-bold text-green-600">
                            {filteredPurchaseHistory.reduce((total, request) => total + (request.cantidadTickets || 0), 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Distribución por Tipo</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">🏍️ Motos</span>
                            <span className="text-sm font-bold text-blue-600">
                              {filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsMotos || 0), 0).toLocaleString()} tickets
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">🚗 Vehículos</span>
                            <span className="text-sm font-bold text-green-600">
                              {filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsVehiculos || 0), 0).toLocaleString()} tickets
                            </span>
                          </div>
                        </div>
                      </div>
                      
                    
                      <div className="bg-orange-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">💰 Valor Monetario</p>
                        <div className="space-y-1">
                          {(() => {
                            const totalMotos = filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsMotos || 0), 0);
                            const totalVehiculos = filteredPurchaseHistory.reduce((total, request) => total + (request.ticketsVehiculos || 0), 0);
                            const valorMotos = totalMotos * 0.25; // 25 centavos por ticket de moto
                            const valorVehiculos = totalVehiculos * 0.40; // 40 centavos por ticket de auto
                            const valorTotal = valorMotos + valorVehiculos;
                            
                            return (
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-gray-500">🏍️ Valor motos:</span>
                                  <span className="text-sm font-medium text-blue-600">
                                    ${valorMotos.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-gray-500">🚗 Valor vehículos:</span>
                                  <span className="text-sm font-medium text-green-600">
                                    ${valorVehiculos.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center border-t pt-1">
                                  <span className="text-xs font-medium text-gray-700">💰 Valor total:</span>
                                  <span className="text-sm font-bold text-orange-600">
                                    ${valorTotal.toFixed(2)}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div className="bg-yellow-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Estado de Solicitudes</p>
                        <div className="space-y-1">
                          {(() => {
                            const statusCount = filteredPurchaseHistory.reduce((acc, request) => {
                              const status = request.status || 'registrado';
                              acc[status] = (acc[status] || 0) + 1;
                              return acc;
                            }, {});
                            
                            return Object.entries(statusCount).map(([status, count]) => (
                              <div key={status} className="flex justify-between items-center">
                                <span className="text-xs text-gray-500 capitalize">{status}:</span>
                                <span className="text-sm font-medium">{count}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Table Section */}
                <div className="lg:col-span-2">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      Mostrando {filteredPurchaseHistory.length} solicitudes {purchaseDateFilter.startDate || purchaseDateFilter.endDate ? 'filtradas' : 'registradas'}
                    </p>
                  </div>
                  
                  {purchaseHistoryLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      {filteredPurchaseHistory.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Solicitud</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Solicitado Por</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad Total</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">🏍️ Motos</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">🚗 Vehículos</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Porcentajes</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notas</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredPurchaseHistory.map((request, index) => (
                              <tr key={request.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {request.id.substring(0, 8)}...
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatTimestamp(request.requestedAt)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {request.requestedBy}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {request.cantidadTickets?.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                                  {request.ticketsMotos?.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                  {request.ticketsVehiculos?.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <div className="text-xs">
                                    <div>Motos: {request.porcentajeMotos}%</div>
                                    <div>Vehículos: {request.porcentajeVehiculos}%</div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                  {request.notes}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No hay solicitudes registradas en el historial</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-4 text-right">
                    <button
                      onClick={() => setShowPurchaseHistory(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for history registration */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Registrar Solicitud de Tickets</h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Registra una solicitud de tickets en el historial para seguimiento.
                </p>
                
                <div className="space-y-4">
                  {/* Cantidad de tickets */}
                  <div>
                    <label htmlFor="historyAmount" className="block text-sm font-medium text-gray-700 mb-2">
                      Cantidad Total de Tickets
                    </label>
                    <input
                      type="number"
                      id="historyAmount"
                      value={historyAmount}
                      onChange={(e) => setHistoryAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ejemplo: 20000"
                      min="1"
                      disabled={historyLoading}
                    />
                  </div>

                  {/* Porcentaje de motos */}
                  <div>
                    <label htmlFor="historyPorcentajeMotos" className="block text-sm font-medium text-gray-700 mb-2">
                      🏍️ Porcentaje de Tickets para Motos (%)
                    </label>
                    <input
                      type="number"
                      id="historyPorcentajeMotos"
                      value={historyPorcentajeMotos}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setHistoryPorcentajeMotos(e.target.value);
                        setHistoryPorcentajeVehiculos((100 - value).toString());
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="45"
                      min="0"
                      max="100"
                      disabled={historyLoading}
                    />
                  </div>

                  {/* Porcentaje de vehículos */}
                  <div>
                    <label htmlFor="historyPorcentajeVehiculos" className="block text-sm font-medium text-gray-700 mb-2">
                      🚗 Porcentaje de Tickets para Vehículos (%)
                    </label>
                    <input
                      type="number"
                      id="historyPorcentajeVehiculos"
                      value={historyPorcentajeVehiculos}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setHistoryPorcentajeVehiculos(e.target.value);
                        setHistoryPorcentajeMotos((100 - value).toString());
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="55"
                      min="0"
                      max="100"
                      disabled={historyLoading}
                    />
                  </div>

                  {/* Resumen visual */}
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm font-medium text-gray-700 mb-2">Resumen de la solicitud:</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-blue-100 p-2 rounded">
                        <span className="font-medium">🏍️ Motos:</span>
                        <br />
                        <span className="text-blue-600 font-bold">
                          {Math.round((parseInt(historyAmount) || 0) * (parseInt(historyPorcentajeMotos) || 0) / 100).toLocaleString()} tickets ({historyPorcentajeMotos}%)
                        </span>
                      </div>
                      <div className="bg-green-100 p-2 rounded">
                        <span className="font-medium">🚗 Vehículos:</span>
                        <br />
                        <span className="text-green-600 font-bold">
                          {Math.round((parseInt(historyAmount) || 0) * (parseInt(historyPorcentajeVehiculos) || 0) / 100).toLocaleString()} tickets ({historyPorcentajeVehiculos}%)
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className={`text-sm font-medium ${(parseInt(historyPorcentajeMotos) + parseInt(historyPorcentajeVehiculos)) === 100 ? 'text-green-600' : 'text-red-600'}`}>
                        Total: {parseInt(historyPorcentajeMotos) + parseInt(historyPorcentajeVehiculos)}%
                        {(parseInt(historyPorcentajeMotos) + parseInt(historyPorcentajeVehiculos)) !== 100 && ' ⚠️ Debe sumar 100%'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={historyLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={saveToHistory}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={historyLoading || !historyAmount || (parseInt(historyPorcentajeMotos) + parseInt(historyPorcentajeVehiculos)) !== 100}
                >
                  {historyLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  )}
                  {historyLoading ? 'Guardando...' : 'Guardar en Historial'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for ticket request */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Solicitar Tickets</h3>
                <button
                  onClick={() => {
                    setShowRequestModal(false);
                    setRequestPassword('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Configura la solicitud de tickets al sistema externo.
                </p>
                
                <div className="space-y-4">
                  {/* Cantidad de tickets */}
                  <div>
                    <label htmlFor="requestAmount" className="block text-sm font-medium text-gray-700 mb-2">
                      Cantidad Total de Tickets
                    </label>
                    <input
                      type="number"
                      id="requestAmount"
                      value={requestAmount}
                      onChange={(e) => setRequestAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ejemplo: 20000"
                      min="1"
                      disabled={requestLoading}
                    />
                  </div>

                  {/* Porcentaje de motos */}
                  <div>
                    <label htmlFor="porcentajeMotos" className="block text-sm font-medium text-gray-700 mb-2">
                      🏍️ Porcentaje de Tickets para Motos (%)
                    </label>
                    <input
                      type="number"
                      id="porcentajeMotos"
                      value={porcentajeMotos}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setPorcentajeMotos(e.target.value);
                        setPorcentajeVehiculos((100 - value).toString());
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="45"
                      min="0"
                      max="100"
                      disabled={requestLoading}
                    />
                  </div>

                  {/* Porcentaje de vehículos */}
                  <div>
                    <label htmlFor="porcentajeVehiculos" className="block text-sm font-medium text-gray-700 mb-2">
                      🚗 Porcentaje de Tickets para Vehículos (%)
                    </label>
                    <input
                      type="number"
                      id="porcentajeVehiculos"
                      value={porcentajeVehiculos}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setPorcentajeVehiculos(e.target.value);
                        setPorcentajeMotos((100 - value).toString());
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="55"
                      min="0"
                      max="100"
                      disabled={requestLoading}
                    />
                  </div>

                  {/* Confirmación de contraseña */}
                  <div>
                    <label htmlFor="requestPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      🔐 Confirmar con tu contraseña
                    </label>
                    <input
                      type="password"
                      id="requestPassword"
                      name="confirmacion-solicitud"
                      value={requestPassword}
                      onChange={(e) => setRequestPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ingresa tu contraseña"
                      disabled={requestLoading}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Se verificará tu contraseña de Firebase antes de procesar la solicitud
                    </p>
                  </div>

                  {/* Resumen visual */}
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm font-medium text-gray-700 mb-2">Resumen de la solicitud:</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-blue-100 p-2 rounded">
                        <span className="font-medium">🏍️ Motos:</span>
                        <br />
                        <span className="text-blue-600 font-bold">
                          {Math.round((parseInt(requestAmount) || 0) * (parseInt(porcentajeMotos) || 0) / 100).toLocaleString()} tickets ({porcentajeMotos}%)
                        </span>
                      </div>
                      <div className="bg-green-100 p-2 rounded">
                        <span className="font-medium">🚗 Vehículos:</span>
                        <br />
                        <span className="text-green-600 font-bold">
                          {Math.round((parseInt(requestAmount) || 0) * (parseInt(porcentajeVehiculos) || 0) / 100).toLocaleString()} tickets ({porcentajeVehiculos}%)
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className={`text-sm font-medium ${(parseInt(porcentajeMotos) + parseInt(porcentajeVehiculos)) === 100 ? 'text-green-600' : 'text-red-600'}`}>
                        Total: {parseInt(porcentajeMotos) + parseInt(porcentajeVehiculos)}%
                        {(parseInt(porcentajeMotos) + parseInt(porcentajeVehiculos)) !== 100 && ' ⚠️ Debe sumar 100%'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRequestModal(false);
                    setRequestPassword('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={requestLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={requestTickets}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={requestLoading || !requestAmount || !requestPassword.trim() || (parseInt(porcentajeMotos) + parseInt(porcentajeVehiculos)) !== 100}
                >
                  {requestLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  )}
                  {requestLoading ? 'Procesando...' : 'Enviar Solicitud'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketsConsorcio; 